import { type HTMLMotionProps, motion } from 'motion/react';
import { forwardRef, type ReactNode } from 'react';

export type ButtonVariant = 'orange' | 'magenta' | 'ink' | 'ghost-light' | 'ghost-dark';

export type ButtonProps = Omit<HTMLMotionProps<'button'>, 'children'> & {
  variant?: ButtonVariant;
  /** Stretches to fill the parent. Default true. */
  fullWidth?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
};

/**
 * Dossier button — the single canonical CTA primitive. Hard 4px-offset
 * shadow, ink border, sharp corners, uppercase display type. Use
 * `'orange'` for the primary CTA, `'magenta'` for Infiltré-side actions,
 * `'ink'` for the filled-dark secondary on cork, `'ghost-light'` over
 * dark stages, `'ghost-dark'` over paper.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'orange', fullWidth = true, leading, trailing, children, className, ...rest },
    ref,
  ) => {
    const palette: Record<ButtonVariant, string> = {
      orange:
        'border-[2.5px] border-ink bg-[var(--color-orange-mark)] text-[var(--color-paper)] shadow-[4px_4px_0_rgba(0,0,0,.35)]',
      magenta:
        'border-[2.5px] border-ink bg-[var(--color-magenta)] text-[var(--color-paper)] shadow-[4px_4px_0_rgba(0,0,0,.35)]',
      ink: 'border-[2.5px] border-ink bg-ink text-[var(--color-paper)] shadow-[4px_4px_0_rgba(0,0,0,.35)]',
      'ghost-light':
        'border-[2.5px] border-[var(--color-paper)] bg-transparent text-[var(--color-paper)] shadow-[4px_4px_0_rgba(0,0,0,.5)]',
      'ghost-dark':
        'border-[2.5px] border-ink bg-transparent text-ink shadow-[4px_4px_0_rgba(0,0,0,.18)]',
    };

    const base =
      'relative isolate inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[3px] px-[clamp(14px,4vw,22px)] py-[14px] font-display text-[clamp(14px,3.6vw,18px)] font-extrabold uppercase tracking-[0.08em] transition-transform disabled:cursor-not-allowed disabled:opacity-40';

    return (
      <motion.button
        ref={ref}
        whileHover={{ x: -1, y: -1 }}
        whileTap={{ x: 2, y: 2 }}
        className={[base, palette[variant], fullWidth ? 'w-full' : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {leading && (
          <span aria-hidden className="relative z-10 flex items-center">
            {leading}
          </span>
        )}
        <span className="relative z-10">{children}</span>
        {trailing && (
          <span aria-hidden className="relative z-10 flex items-center">
            {trailing}
          </span>
        )}
      </motion.button>
    );
  },
);

Button.displayName = 'Button';
